'use client';

import ImageOptimizer from '@/components/image-optimizer';
import useBodyScrollLock from '@/hooks/use-body-scroll-lock';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { MAIN_SECTION_PORTAL_TARGET_ID } from '@/constants';
import 'swiper/css';
import { Pagination, Zoom } from 'swiper/modules';

interface ImageViewerProps {
  images: Array<{ savePath: string }>;
  initialIndex?: number;
  open: boolean;
  onClose: () => void;
  showIndex?: boolean;
}

export default function ImageViewer({
  images,
  initialIndex = 0,
  open,
  onClose,
  showIndex = true,
}: ImageViewerProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const swiperRef = useRef<SwiperType | null>(null);
  const portalTarget =
    open && typeof document !== 'undefined'
      ? document.getElementById(MAIN_SECTION_PORTAL_TARGET_ID)
      : null;

  useBodyScrollLock(open);

  useEffect(() => {
    if (open) {
      // Swiper가 초기화될 때까지 약간의 지연을 두고 실행
      const timer = setTimeout(() => {
        if (swiperRef.current) {
          if (images.length > 1) {
            swiperRef.current.slideToLoop(initialIndex, 0);
          } else {
            swiperRef.current.slideTo(initialIndex, 0);
          }
          setCurrentIndex(initialIndex);
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [open, initialIndex, images.length]);

  const handleSlideChange = (swiper: SwiperType) => {
    const realIndex = images.length > 1 ? swiper.realIndex : swiper.activeIndex;
    setCurrentIndex(realIndex);
  };

  if (!open) return null;

  const overlayClassName = portalTarget
    ? 'absolute inset-0 z-[9999] bg-black flex flex-col'
    : 'fixed inset-0 z-[9999] bg-black flex flex-col';

  const viewerContent = (
    <div className={overlayClassName}>
      {/* Header */}
      <div
        className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-5 py-4 bg-black/50"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex-1" />
        <div className="flex-1 flex items-center justify-center">
          {showIndex && (
            <span className="typo-16 font-medium text-white">
              {currentIndex + 1} / {images.length}
            </span>
          )}
        </div>
        <div className="flex-1 flex justify-end">
          <button
            onClick={onClose}
            className="flex items-center justify-center p-2 cursor-pointer"
            aria-label="닫기"
          >
            <ImageOptimizer
              src="/images/academy/review/icon_image_close.svg"
              alt="닫기"
              width={16}
              height={16}
            />
          </button>
        </div>
      </div>

      {/* Image Container */}
      <div className="flex-1 flex items-center justify-center relative overflow-hidden">
        <Swiper
          modules={[Pagination, Zoom]}
          slidesPerView={1}
          spaceBetween={0}
          loop={images.length > 1}
          grabCursor
          zoom={{
            maxRatio: 3, // 최대 확대 배율
            minRatio: 1, // 최소 배율
            toggle: true, // 더블탭 줌
          }}
          onSlideChange={handleSlideChange}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
            // Swiper 초기화 시 initialIndex로 이동
            if (open && initialIndex >= 0 && initialIndex < images.length) {
              if (images.length > 1) {
                swiper.slideToLoop(initialIndex, 0);
              } else {
                swiper.slideTo(initialIndex, 0);
              }
              setCurrentIndex(initialIndex);
            }

            // 줌 상태 변경 감지
            swiper.on('zoomChange', (_, scale) => {
              swiper.allowTouchMove = scale <= 1;
            });
          }}
          className="h-full w-full"
          touchStartPreventDefault={false}
          resistanceRatio={0}
        >
          {images.map((image, index) => (
            <SwiperSlide key={`image-${image.savePath}-${index}`}>
              <div className="relative w-full h-full swiper-zoom-container">
                <ImageOptimizer
                  key={`${image.savePath}-${index}`}
                  src={image.savePath}
                  alt={`이미지 ${index + 1}`}
                  fill
                  className="object-contain select-none"
                  sizes="100vw"
                  unoptimized
                  loading={index === currentIndex ? 'eager' : 'lazy'}
                  draggable={false}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );

  if (portalTarget) {
    return createPortal(viewerContent, portalTarget);
  }

  return viewerContent;
}
